import { useEffect, useState } from "react";
import api, { formatPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, Image as ImageIcon, MapPin, Users, Building2, TrendingUp, Inbox, CheckCircle2, XCircle, Star, Trash2, Plus, UserPlus, CreditCard, BarChart3, ListPlus } from "lucide-react";

const EMPTY_LISTING = { title: "", mode: "rent", category: "residential", property_type: "Apartment", city: "", pin_code: "", state: "", bhk: "", furnishing: "unfurnished", price: "", area_sqft: "", owner_name: "", owner_phone: "", description: "", imagesText: "" };

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [props_, setProps] = useState([]);
  const [users, setUsers] = useState([]);
  const [hero, setHero] = useState([]);
  const [locations, setLocations] = useState([]);
  const [ticker, setTicker] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [newHero, setNewHero] = useState({ url: "", title: "", subtitle: "" });
  const [newCity, setNewCity] = useState({ city: "", state: "" });
  const [newPin, setNewPin] = useState({ city: "", pin: "", locality: "" });
  const [newTicker, setNewTicker] = useState("");
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "broker" });
  const [listing, setListing] = useState(EMPTY_LISTING);
  const [payments, setPayments] = useState([]);
  const [searches, setSearches] = useState([]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/hero-images").then((r) => setHero(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/ticker").then((r) => setTicker(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/admin/search-reports").then((r) => setSearches(r.data)).catch(() => {});
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user || user.role !== "admin") {
    return (
      <div className="mx-auto max-w-7xl px-4 py-24 text-center" data-testid="admin-denied">
        <ShieldCheck className="mx-auto h-12 w-12 text-slate-300" />
        <p className="mt-4 text-muted-foreground">Admin access required. Sign in with the admin account.</p>
      </div>
    );
  }

  const patchProp = async (id, patch) => {
    await api.patch(`/admin/properties/${id}`, patch);
    toast.success("Listing updated");
    load();
  };

  const statCard = (icon: any, label, value, tid) => (
    <div className="rounded-2xl border bg-card p-5" data-testid={tid}>
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-blue-50 p-2.5">{icon}</div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="font-price text-2xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-panel">
      <h1 className="font-heading flex items-center gap-3 text-3xl font-bold tracking-tight">
        <ShieldCheck className="h-8 w-8 text-violet-600" /> Admin Panel
      </h1>

      <Tabs defaultValue="overview" className="mt-6">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="add-user" data-testid="admin-tab-add-user">Add User / Broker</TabsTrigger>
          <TabsTrigger value="add-listing" data-testid="admin-tab-add-listing">Add Listing</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="admin-tab-subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="search-reports" data-testid="admin-tab-search-reports">Search Reports</TabsTrigger>
          <TabsTrigger value="listings" data-testid="admin-tab-listings">Listings</TabsTrigger>
          <TabsTrigger value="carousel" data-testid="admin-tab-carousel">Hero Carousel</TabsTrigger>
          <TabsTrigger value="locations" data-testid="admin-tab-locations">Locations</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="ticker" data-testid="admin-tab-ticker">Ticker</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          {stats && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {statCard(<Users className="h-5 w-5 text-blue-600" />, "Total Users", stats.users, "stat-users")}
              {statCard(<Badge className="h-5 w-5" />, "₹99 Members", stats.members, "stat-members")}
              {statCard(<Building2 className="h-5 w-5 text-blue-600" />, "Live Listings", stats.approved_listings, "stat-listings")}
              {statCard(<Inbox className="h-5 w-5 text-blue-600" />, "Pending Review", stats.pending_listings, "stat-pending")}
              {statCard(<TrendingUp className="h-5 w-5 text-emerald-600" />, "Revenue", `₹${stats.revenue_inr.toLocaleString("en-IN")}`, "stat-revenue")}
              {statCard(<Inbox className="h-5 w-5 text-blue-600" />, "Enquiries", stats.enquiries, "stat-enquiries")}
            </div>
          )}
        </TabsContent>

        <TabsContent value="add-user" className="mt-6">
          <div className="max-w-lg rounded-xl border bg-card p-6" data-testid="admin-add-user-form">
            <h3 className="font-heading flex items-center gap-2 font-bold"><UserPlus className="h-5 w-5 text-blue-600" /> Create User / Broker Account</h3>
            <div className="mt-4 space-y-3">
              <div className="space-y-1">
                <Label>Full name</Label>
                <Input data-testid="admin-new-user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Broker / user name" />
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input data-testid="admin-new-user-email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@example.com" />
              </div>
              <div className="space-y-1">
                <Label>Password</Label>
                <Input data-testid="admin-new-user-password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Set a password" />
              </div>
              <div className="space-y-1">
                <Label>Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger data-testid="admin-new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["seeker", "owner", "agent", "broker", "builder", "admin"].map((r) => (
                      <SelectItem key={r} value={r} data-testid={`admin-role-${r}`}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="admin-add-user-submit" className="w-full gap-1" onClick={async () => {
                if (!newUser.name || !newUser.email || !newUser.password) return toast.error("All fields required");
                try {
                  await api.post("/admin/users", newUser);
                  toast.success(`${newUser.role} account created`);
                  setNewUser({ name: "", email: "", password: "", role: "broker" });
                  load();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Could not create user");
                }
              }}><Plus className="h-4 w-4" /> Create Account</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="add-listing" className="mt-6">
          <div className="rounded-xl border bg-card p-6" data-testid="admin-add-listing-form">
            <h3 className="font-heading flex items-center gap-2 font-bold"><ListPlus className="h-5 w-5 text-emerald-600" /> Add Listing (goes live instantly, verified)</h3>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-1 md:col-span-3">
                <Label>Title</Label>
                <Input data-testid="admin-listing-title" value={listing.title} onChange={(e) => setListing({ ...listing, title: e.target.value })} placeholder="e.g. 2BHK semi-furnished, Patia" />
              </div>
              <div className="space-y-1">
                <Label>Mode</Label>
                <Select value={listing.mode} onValueChange={(v) => setListing({ ...listing, mode: v })}>
                  <SelectTrigger data-testid="admin-listing-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="buy">Buy</SelectItem>
                    <SelectItem value="sale">Sale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={listing.category} onValueChange={(v) => setListing({ ...listing, category: v })}>
                  <SelectTrigger data-testid="admin-listing-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="land">Land / Plot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Property type</Label>
                <Select value={listing.property_type} onValueChange={(v) => setListing({ ...listing, property_type: v })}>
                  <SelectTrigger data-testid="admin-listing-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Apartment", "Independent House", "Villa", "Builder Floor", "PG / Room", "Office Space", "Co-working Space", "Virtual Office", "Desks", "Shop", "Warehouse", "Residential Plot", "Agricultural Land", "Industrial Property"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>City</Label>
                <Select value={listing.city} onValueChange={(v) => setListing({ ...listing, city: v, pin_code: "", state: locations.find((l) => l.city === v)?.state || "" })}>
                  <SelectTrigger data-testid="admin-listing-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.city} value={l.city}>{l.city}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Locality / PIN</Label>
                <Select value={listing.pin_code} onValueChange={(v) => setListing({ ...listing, pin_code: v })} disabled={!listing.city}>
                  <SelectTrigger data-testid="admin-listing-pin"><SelectValue placeholder="Select PIN" /></SelectTrigger>
                  <SelectContent>
                    {(locations.find((l) => l.city === listing.city)?.pincodes || []).map((p) => (
                      <SelectItem key={p.pin} value={p.pin}>{p.locality} · {p.pin}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Furnishing</Label>
                <Select value={listing.furnishing} onValueChange={(v) => setListing({ ...listing, furnishing: v })}>
                  <SelectTrigger data-testid="admin-listing-furnishing"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fully-furnished">Fully Furnished</SelectItem>
                    <SelectItem value="semi-furnished">Semi Furnished</SelectItem>
                    <SelectItem value="unfurnished">Unfurnished</SelectItem>
                    <SelectItem value="raw">Raw / Bare Shell</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>BHK (optional)</Label>
                <Select value={listing.bhk} onValueChange={(v) => setListing({ ...listing, bhk: v })}>
                  <SelectTrigger data-testid="admin-listing-bhk"><SelectValue placeholder="N/A" /></SelectTrigger>
                  <SelectContent>{["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5+ BHK"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>{listing.mode === "rent" ? "Monthly rent ₹" : "Price ₹"}</Label>
                <Input data-testid="admin-listing-price" type="number" value={listing.price} onChange={(e) => setListing({ ...listing, price: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Area (sq.ft)</Label>
                <Input data-testid="admin-listing-area" type="number" value={listing.area_sqft} onChange={(e) => setListing({ ...listing, area_sqft: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Owner name</Label>
                <Input data-testid="admin-listing-owner-name" value={listing.owner_name} onChange={(e) => setListing({ ...listing, owner_name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Owner phone</Label>
                <Input data-testid="admin-listing-owner-phone" value={listing.owner_phone} onChange={(e) => setListing({ ...listing, owner_phone: e.target.value })} placeholder="+91 ..." />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Description</Label>
                <Textarea data-testid="admin-listing-description" rows={2} value={listing.description} onChange={(e) => setListing({ ...listing, description: e.target.value })} />
              </div>
              <div className="space-y-1 md:col-span-3">
                <Label>Photo URLs (one per line, up to 6)</Label>
                <Textarea data-testid="admin-listing-images" rows={3} value={listing.imagesText} onChange={(e) => setListing({ ...listing, imagesText: e.target.value })} placeholder="https://...&#10;https://..." />
              </div>
            </div>
            <Button data-testid="admin-add-listing-submit" className="mt-4 gap-1 bg-emerald-600 text-white hover:bg-emerald-700" onClick={async () => {
              if (!listing.title || !listing.city || !listing.price) return toast.error("Title, city and price are required");
              try {
                const loc = locations.find((l) => l.city === listing.city);
                const pinDoc = loc?.pincodes.find((p) => p.pin === listing.pin_code);
                await api.post("/admin/properties", {
                  title: listing.title, mode: listing.mode, category: listing.category,
                  property_type: listing.property_type, bhk: listing.bhk,
                  price: Number(listing.price), area_sqft: Number(listing.area_sqft) || 0,
                  furnishing: listing.furnishing, city: listing.city,
                  locality: pinDoc?.locality || "", pin_code: listing.pin_code, state: listing.state,
                  description: listing.description, owner_name: listing.owner_name, owner_phone: listing.owner_phone,
                  images: listing.imagesText.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 6),
                });
                toast.success("Listing published (approved + verified)");
                setListing(EMPTY_LISTING);
                load();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Could not create listing");
              }
            }}><Plus className="h-4 w-4" /> Publish Listing</Button>
          </div>
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-6">
          <div className="mb-4 flex flex-wrap gap-4 rounded-xl border bg-card p-4" data-testid="subscriptions-summary">
            <div><p className="text-xs text-muted-foreground">Total transactions</p><p className="font-price text-xl font-bold">{payments.length}</p></div>
            <div><p className="text-xs text-muted-foreground">Paid memberships</p><p className="font-price text-xl font-bold text-emerald-700">{payments.filter((p) => p.payment_status === "paid").length}</p></div>
            <div><p className="text-xs text-muted-foreground">Revenue</p><p className="font-price text-xl font-bold text-emerald-700">₹{payments.filter((p) => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0).toLocaleString("en-IN")}</p></div>
          </div>
          <div className="space-y-2" data-testid="admin-subscriptions-list">
            {payments.map((t) => (
              <div key={t.session_id} className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3 text-sm" data-testid={`subscription-${t.session_id.slice(-8)}`}>
                <CreditCard className="h-4 w-4 text-violet-600" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{t.user_name || t.user_email || "Unknown user"}</p>
                  <p className="font-price text-xs text-muted-foreground">{t.session_id.slice(0, 24)}…</p>
                </div>
                <span className="font-price font-bold">₹{t.amount}</span>
                <Badge variant={t.payment_status === "paid" ? "default" : "secondary"} className={t.payment_status === "paid" ? "bg-emerald-600" : ""}>{t.payment_status}</Badge>
                <span className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("en-IN")}</span>
              </div>
            ))}
            {payments.length === 0 && <p className="text-sm text-muted-foreground">No payment transactions yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="search-reports" className="mt-6">
          <div className="space-y-3" data-testid="admin-search-reports-list">
            {Object.values(
              searches.reduce((acc, e) => {
                const key = e.user_email || "anonymous";
                if (!acc[key]) acc[key] = { email: key, count: 0, last: e.created_at, recent: [] };
                acc[key].count += 1;
                if (acc[key].recent.length < 3) acc[key].recent.push(e.filters);
                return acc;
              }, {})
            ).map((r) => (
              <div key={r.email} className="rounded-xl border bg-card p-4" data-testid={`search-report-${r.email.replace(/[^a-z0-9]/gi, "-")}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                  <p className="text-sm font-semibold">{r.email}</p>
                  <Badge variant="secondary">{r.count} searches</Badge>
                  <span className="text-xs text-muted-foreground">Last: {new Date(r.last).toLocaleString("en-IN")}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {r.recent.map((f, i) => (
                    <span key={i} className="rounded-full bg-secondary px-3 py-1 font-price text-xs">
                      {Object.entries(f).map(([k, v]) => `${k}:${v}`).join(" · ")}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {searches.length === 0 && <p className="text-sm text-muted-foreground">No search activity recorded yet. Searches with filters are tracked automatically.</p>}
          </div>
        </TabsContent>

        <TabsContent value="listings" className="mt-6">
          <div className="space-y-3" data-testid="admin-listings-list">
            {props_.map((p) => (
              <div key={p.property_id} className="flex flex-wrap items-center gap-4 rounded-xl border bg-card p-4" data-testid={`admin-listing-${p.property_id}`}>
                {p.images?.[0] && <img src={p.images[0]} alt="" className="h-14 w-20 rounded-lg object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{p.title}</p>
                  <p className="text-xs text-muted-foreground">{p.city} · {p.pin_code} · {formatPrice(p.price, p.mode)} · {p.mode.toUpperCase()}</p>
                </div>
                <Badge variant={p.status === "approved" ? "default" : p.status === "pending" ? "secondary" : "destructive"}>{p.status}</Badge>
                {p.featured && <Badge className="bg-amber-500">Featured</Badge>}
                {p.verified && <Badge className="bg-emerald-600">Verified</Badge>}
                <div className="flex gap-2">
                  {p.status !== "approved" && (
                    <Button size="sm" data-testid={`approve-${p.property_id}`} className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => patchProp(p.property_id, { status: "approved" })}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                    </Button>
                  )}
                  {p.status !== "rejected" && (
                    <Button size="sm" variant="outline" data-testid={`reject-${p.property_id}`} className="gap-1" onClick={() => patchProp(p.property_id, { status: "rejected" })}>
                      <XCircle className="h-3.5 w-3.5" /> Reject
                    </Button>
                  )}
                  <Button size="sm" variant="outline" data-testid={`feature-${p.property_id}`} className="gap-1" onClick={() => patchProp(p.property_id, { featured: !p.featured })}>
                    <Star className="h-3.5 w-3.5" /> {p.featured ? "Unfeature" : "Feature"}
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`verify-${p.property_id}`} onClick={() => patchProp(p.property_id, { verified: !p.verified })}>
                    {p.verified ? "Unverify" : "Verify"}
                  </Button>
                  <Button size="sm" variant="destructive" data-testid={`delete-${p.property_id}`} onClick={async () => { await api.delete(`/admin/properties/${p.property_id}`); toast.success("Deleted"); load(); }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="carousel" className="mt-6">
          <div className="rounded-xl border bg-card p-5">
            <h3 className="font-heading flex items-center gap-2 font-bold"><ImageIcon className="h-5 w-5 text-blue-600" /> Add hero banner image</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <Input data-testid="admin-hero-url-input" placeholder="Image URL" value={newHero.url} onChange={(e) => setNewHero({ ...newHero, url: e.target.value })} />
              <Input data-testid="admin-hero-title-input" placeholder="Title" value={newHero.title} onChange={(e) => setNewHero({ ...newHero, title: e.target.value })} />
              <Button data-testid="admin-add-carousel-image-button" className="gap-1" onClick={async () => {
                if (!newHero.url) return toast.error("Image URL required");
                await api.post("/admin/hero-images", newHero);
                setNewHero({ url: "", title: "", subtitle: "" });
                toast.success("Hero image added");
                load();
              }}><Plus className="h-4 w-4" /> Add Image</Button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
            {hero.map((h) => (
              <div key={h.image_id} className="group relative overflow-hidden rounded-xl border" data-testid={`admin-hero-${h.image_id}`}>
                <img src={h.url} alt={h.title} className="h-32 w-full object-cover" />
                <button data-testid={`admin-hero-delete-${h.image_id}`}
                  className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={async () => { await api.delete(`/admin/hero-images/${h.image_id}`); toast.success("Removed"); load(); }}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
                <p className="p-2 text-xs font-medium">{h.title || "Untitled"}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="locations" className="mt-6">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-heading flex items-center gap-2 font-bold"><MapPin className="h-5 w-5 text-blue-600" /> Add city</h3>
              <div className="mt-3 flex gap-2">
                <Input data-testid="admin-city-input" placeholder="City" value={newCity.city} onChange={(e) => setNewCity({ ...newCity, city: e.target.value })} />
                <Input data-testid="admin-state-input" placeholder="State" value={newCity.state} onChange={(e) => setNewCity({ ...newCity, state: e.target.value })} />
                <Button data-testid="admin-add-city-button" onClick={async () => {
                  await api.post("/admin/locations", newCity);
                  setNewCity({ city: "", state: "" });
                  toast.success("City added"); load();
                }}>Add</Button>
              </div>
              <h3 className="font-heading mt-6 font-bold">Add PIN code to city</h3>
              <div className="mt-3 flex gap-2">
                <Input data-testid="admin-pin-city-input" placeholder="City" value={newPin.city} onChange={(e) => setNewPin({ ...newPin, city: e.target.value })} />
                <Input data-testid="admin-pin-input" placeholder="PIN" value={newPin.pin} onChange={(e) => setNewPin({ ...newPin, pin: e.target.value })} />
                <Input data-testid="admin-pin-locality-input" placeholder="Locality" value={newPin.locality} onChange={(e) => setNewPin({ ...newPin, locality: e.target.value })} />
                <Button data-testid="admin-add-pin-button" onClick={async () => {
                  await api.post(`/admin/locations/${newPin.city}/pincodes`, { pin: newPin.pin, locality: newPin.locality });
                  setNewPin({ city: "", pin: "", locality: "" });
                  toast.success("PIN added"); load();
                }}>Add</Button>
              </div>
            </div>
            <div className="space-y-2" data-testid="admin-locations-list">
              {locations.map((l) => (
                <div key={l.city} className="flex items-center justify-between rounded-xl border bg-card p-3">
                  <div>
                    <p className="text-sm font-semibold">{l.city} <span className="text-xs text-muted-foreground">{l.state}</span></p>
                    <p className="font-price text-xs text-muted-foreground">{l.pincodes.map((p) => `${p.locality} ${p.pin}`).join(" · ") || "No PINs"}</p>
                  </div>
                  <Button size="sm" variant="ghost" data-testid={`admin-city-delete-${l.city}`} onClick={async () => { await api.delete(`/admin/locations/${l.city}`); load(); }}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="space-y-2" data-testid="admin-users-list">
            {users.map((u) => (
              <div key={u.user_id} className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3" data-testid={`admin-user-${u.user_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{u.name || u.email}</p>
                  <p className="text-xs text-muted-foreground">{u.email} · {u.role}</p>
                </div>
                {u.is_member && <Badge className="bg-violet-600">Member</Badge>}
                {u.is_verified && <Badge className="bg-emerald-600">Verified</Badge>}
                {u.suspended && <Badge variant="destructive">Suspended</Badge>}
                <Button size="sm" variant="outline" data-testid={`admin-user-suspend-${u.user_id}`}
                  onClick={async () => { await api.patch(`/admin/users/${u.user_id}`, { suspended: !u.suspended }); load(); }}>
                  {u.suspended ? "Unsuspend" : "Suspend"}
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="ticker" className="mt-6">
          <div className="flex gap-2">
            <Input data-testid="admin-ticker-input" placeholder="New market insight..." value={newTicker} onChange={(e) => setNewTicker(e.target.value)} />
            <Button data-testid="admin-add-ticker-button" onClick={async () => {
              if (!newTicker) return;
              await api.post("/admin/ticker", { text: newTicker });
              setNewTicker(""); toast.success("Ticker added"); load();
            }}>Add</Button>
          </div>
          <div className="mt-4 space-y-2">
            {ticker.map((t) => (
              <div key={t.ticker_id} className="flex items-center justify-between rounded-xl border bg-card p-3 text-sm">
                <span>{t.text}</span>
                <Button size="sm" variant="ghost" data-testid={`admin-ticker-delete-${t.ticker_id}`} onClick={async () => { await api.delete(`/admin/ticker/${t.ticker_id}`); load(); }}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="enquiries" className="mt-6">
          <div className="space-y-2" data-testid="admin-enquiries-list">
            {enquiries.map((e) => (
              <div key={e.enquiry_id} className="rounded-xl border bg-card p-4 text-sm">
                <p className="font-semibold">{e.name} <span className="font-price text-xs text-muted-foreground">{e.phone}</span></p>
                <p className="mt-1 text-muted-foreground">{e.message || "—"}</p>
                <p className="mt-1 text-xs text-blue-600">Property: {e.property_id}</p>
              </div>
            ))}
            {enquiries.length === 0 && <p className="text-sm text-muted-foreground">No enquiries yet.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
